import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import { logError } from '~publish/legacy/utils/logError'

export const TransferOrganizationOwnershipMutation = graphql(/* GraphQL */ `
  mutation TransferOrganizationOwnership(
    $organizationId: OrganizationId!
    $newOwner: AccountId!
  ) {
    transferOrganizationOwnership(
      input: { organizationId: $organizationId, newOwner: $newOwner }
    ) {
      __typename
      ... on EmptySuccess {
        _empty
      }
      ... on TransferError {
        message
      }
    }
  }
`)

type TransferOrganizationOwnership = ({
  organizationId,
  newOwner,
}: {
  organizationId: string
  newOwner: string
}) => Promise<{ success: boolean; errorMessage?: string }>

type TransferOrganizationOwnershipResponse = {
  transferOrganizationOwnership: TransferOrganizationOwnership
  isLoading: boolean
  hasError: boolean
}

/**
 * Custom hook to transfer organization ownership to a new account.
 * @returns {TransferOrganizationOwnershipResponse} The function to trigger the transfer.
 */
export const useTransferOrganizationOwnership =
  (): TransferOrganizationOwnershipResponse => {
    const [executeTransfer, { loading, error }] = useMutation(
      TransferOrganizationOwnershipMutation,
    )

    const transferOrganizationOwnership = useCallback(
      async ({
        organizationId,
        newOwner,
      }: {
        organizationId: string
        newOwner: string
      }) => {
        try {
          const { data, error: mutationError } = await executeTransfer({
            variables: {
              organizationId,
              newOwner,
            },
          })

          if (mutationError) {
            const unexpectedError = new Error(
              mutationError.message || 'Unexpected API response',
            )
            logError(unexpectedError, {
              metaData: { data, error: mutationError },
            })
            toast.error(
              'An unexpected error occurred while transferring account ownership. Please try again.',
            )
            return {
              success: false,
              errorMessage:
                'An unexpected error occurred while transferring account ownership. Please try again.',
            }
          }

          const payload = data?.transferOrganizationOwnership
          if (payload?.__typename === 'TransferError') {
            toast.error(payload.message)
            return { success: false, errorMessage: payload.message }
          }

          toast.success(
            'Account ownership transferred successfully. This page will reload in 5 seconds.',
          )
          return { success: true }
        } catch (transferError) {
          toast.error(
            'An error occurred while transferring account ownership. Please try again.',
          )
          logError(
            transferError instanceof Error
              ? transferError
              : new Error(String(transferError)),
            {
              metaData: { variables: { organizationId, newOwner } },
            },
          )
          return {
            success: false,
            errorMessage:
              'An error occurred while transferring account ownership. Please try again.',
          }
        }
      },
      [executeTransfer],
    )

    return {
      transferOrganizationOwnership,
      isLoading: loading,
      hasError: !!error,
    }
  }
